import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { DualViewSwitcher } from "../components/DualViewSwitcher";
import {
  ArrowLeft, ArrowUp, ArrowDown, Minus, Download, Loader2, Target,
  Users, TrendingUp, Heart, XCircle, MapPin, ListChecks, Activity,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

const PERIODS = [
  { v: "today", l: "Today" },
  { v: "week", l: "7 Days" },
  { v: "month", l: "30 Days" },
  { v: "quarter", l: "Quarter" },
  { v: "year", l: "Year" },
];

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function Delta({ d }) {
  if (!d) return null;
  const up = d.direction === "up", down = d.direction === "down";
  const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
  const cls = up ? "text-emerald-600" : down ? "text-rose-600" : "text-slate-400";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${cls}`} data-testid="perf-delta">
      <Icon className="h-3 w-3" />
      {d.pct === null ? "new" : `${Math.abs(d.pct)}%`}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, delta, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1.5">
          <Icon className="h-4 w-4 text-emerald-600" />
          {delta && <Delta d={delta} />}
        </div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      </CardContent>
    </Card>
  );
}

export default function MyPerformanceRecord() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/fundraiser/performance?period=${period}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load your performance record");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const exportReport = async () => {
    setExporting(true);
    try {
      const resp = await apiClient.get(`/fundraiser/performance-report/export?period=${period}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/html" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-performance-report-${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("My Performance Report downloaded");
    } catch (e) {
      toast.error("Report export failed");
    } finally {
      setExporting(false);
    }
  };

  const s = data?.summary || {};
  const pulse = data?.pulse || {};
  const pl = data?.pipeline || {};
  const pr = data?.projection || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-performance-record">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="h-9 px-2" data-testid="perf-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading font-bold text-lg sm:text-xl text-slate-900">My Performance Record</h1>
            <p className="text-xs text-slate-500">Your own fundraising activity and results — just for you</p>
          </div>
          <Button onClick={exportReport} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="perf-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 sm:mr-2" />}
            <span className="hidden sm:inline">{exporting ? "Exporting…" : "Export My Performance Report"}</span>
          </Button>
          <DualViewSwitcher />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Period filter */}
        <div className="flex flex-wrap gap-2" data-testid="perf-period-filter">
          {PERIODS.map((p) => (
            <Button
              key={p.v}
              size="sm"
              variant={period === p.v ? "default" : "outline"}
              onClick={() => setPeriod(p.v)}
              className={period === p.v ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
              data-testid={`perf-period-${p.v}`}
            >
              {p.l}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center" data-testid="perf-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your performance…
          </div>
        ) : (
          <>
            {/* Section A — Personal Performance Summary */}
            <section data-testid="perf-section-summary">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">My Performance Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Activity} label="Conversations" value={s.conversations ?? 0} delta={pulse.conversations} testid="perf-card-conversations" />
                <StatCard icon={Users} label="Sign-ups" value={s.signups ?? 0} delta={pulse.signups} testid="perf-card-signups" />
                <StatCard icon={Heart} label="Active Donors" value={s.active ?? 0} testid="perf-card-active" />
                <StatCard icon={XCircle} label="Cancellations" value={s.cancellations ?? 0} testid="perf-card-cancellations" />
                <StatCard icon={TrendingUp} label="Attributed Value" value={gbp(s.attributedValueGBP)} delta={pulse.attributedValueGBP} testid="perf-card-value" />
                <StatCard icon={Target} label="Conversion Rate" value={`${s.conversionRatePct ?? 0}%`} delta={pulse.conversionRatePct} testid="perf-card-rate" />
                <StatCard icon={Heart} label="Avg Gift" value={gbp(s.avgGiftGBP)} testid="perf-card-avg" />
                <StatCard icon={Users} label="Delivered" value={pl.delivered ?? 0} testid="perf-card-delivered" />
              </div>
            </section>

            {/* Section C — My Donor Pipeline */}
            <section data-testid="perf-section-pipeline">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">My Donor Pipeline</h2>
              <Card className="border-slate-200">
                <CardContent className="p-4 grid grid-cols-3 md:grid-cols-6 gap-3 text-center">
                  {[
                    ["Conversations", pl.conversations], ["Sign-ups", pl.signups],
                    ["Pending", pl.pending], ["Active", pl.active],
                    ["Delivered", pl.delivered], ["Cancelled", pl.cancellations],
                  ].map(([l, v]) => (
                    <div key={l}>
                      <div className="text-xl font-bold text-slate-900">{v ?? 0}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </section>

            {/* Section D — My Targets vs Actual */}
            <section data-testid="perf-section-targets">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">My Targets</h2>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  {!data?.targets?.hasTargets ? (
                    <p className="text-sm text-slate-500" data-testid="perf-no-targets">
                      No targets set yet. Ask your manager to set sign-up and value targets so you can track progress here.
                    </p>
                  ) : null}
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    {[
                      ["Sign-ups", data?.targets?.signups],
                      ["Attributed Value", data?.targets?.attributedValueGBP],
                      ["Conversion Rate", data?.targets?.conversionRatePct],
                    ].map(([l, t]) => (
                      <div key={l} className="rounded-lg border border-slate-100 p-3">
                        <div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div>
                        <div className="text-lg font-bold text-slate-900">{t?.actual ?? 0}</div>
                        <div className="text-[11px] text-slate-400">Target: {t?.target ?? "—"}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Section E — My Trend / Forecast */}
            <section data-testid="perf-section-trend">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">My Trend</h2>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  {(data?.trend || []).length > 1 ? (
                    <div style={{ width: "100%", height: 220, minHeight: 220 }}>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={data.trend} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                          <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                          <Tooltip />
                          <Line type="monotone" dataKey="signups" stroke="#10b981" strokeWidth={2} dot={false} name="Sign-ups" />
                          <Line type="monotone" dataKey="conversations" stroke="#6366f1" strokeWidth={2} dot={false} name="Conversations" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Not enough activity to chart a trend yet.</p>
                  )}
                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm text-slate-600" data-testid="perf-projection">
                    Based on {pr.basis}: ~<b>{pr.projectedSignupsNext7 ?? 0}</b> sign-ups projected next 7 days,
                    ~<b>{pr.projectedSignupsNext30 ?? 0}</b> next 30 days.
                    <Badge className="ml-2 bg-slate-100 text-slate-600 capitalize">{pr.confidence} confidence</Badge>
                    {pr.lowData && <span className="text-amber-600 ml-2">Low confidence due to limited recent data.</span>}
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Section F — My Campaign Breakdown */}
            <section data-testid="perf-section-breakdown">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">My Campaign Breakdown</h2>
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  {(data?.campaignBreakdown || []).length === 0 ? (
                    <p className="text-sm text-slate-500 p-4">No campaign activity in this period.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                          <th className="text-left p-3">Campaign</th>
                          <th className="text-right p-3">Sign-ups</th>
                          <th className="text-right p-3">Value</th>
                          <th className="text-right p-3">Cancelled</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.campaignBreakdown.map((c) => (
                          <tr key={c.campaignId} className="border-b border-slate-50" data-testid={`perf-campaign-row-${c.campaignId}`}>
                            <td className="p-3 text-slate-900">{c.campaign}</td>
                            <td className="p-3 text-right">{c.signups}</td>
                            <td className="p-3 text-right">{gbp(c.attributedValueGBP)}</td>
                            <td className="p-3 text-right text-rose-600">{c.cancellations}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
              {(data?.topRoads || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="perf-top-roads">
                  {data.topRoads.map((r) => (
                    <Badge key={r.road} className="bg-emerald-50 text-emerald-700 gap-1">
                      <MapPin className="h-3 w-3" /> {r.road} · {r.conversions}
                    </Badge>
                  ))}
                </div>
              )}
            </section>

            {/* Section G — My Actions */}
            <section data-testid="perf-section-actions">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">My Actions</h2>
              <div className="space-y-2">
                {(data?.actions || []).length === 0 ? (
                  <Card className="border-slate-200"><CardContent className="p-4 text-sm text-slate-500 flex items-center gap-2" data-testid="perf-no-actions"><ListChecks className="h-4 w-4 text-emerald-600" /> Nothing outstanding — great work.</CardContent></Card>
                ) : data.actions.map((a, i) => (
                  <Card key={i} className={`border-l-4 ${a.severity === "high" ? "border-l-rose-500" : a.severity === "medium" ? "border-l-amber-500" : "border-l-slate-300"} border-slate-200`} data-testid={`perf-action-${a.type}`}>
                    <CardContent className="p-3">
                      <div className="font-medium text-sm text-slate-900">{a.title}</div>
                      <div className="text-xs text-slate-500">{a.body}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
